/**
 * Career DNA™ — the seven-strand framework, rendered with build status.
 *
 * Presentation only. Live strands show the value the sealed engine payload
 * already carries; reserved and in-build strands show a status sentence and
 * never a number. Status is carried by text and shape (solid vs dashed
 * outline), never by colour alone.
 */
import { CAREER_DNA_FRAMEWORK_INTRO, CAREER_DNA_STRANDS, STRAND_STATUS_LABEL, type PublicStrand } from "@/features/career-dna/strandCatalogue";
import { bar, pct, type IntelRow } from "./reportTypes";

function StatusDot({ status }: { status: PublicStrand["status"] }) {
  return status === "live" ? (
    <span
      aria-hidden="true"
      className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.7)]"
    />
  ) : (
    <span
      aria-hidden="true"
      className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border border-dashed border-muted-foreground/70"
    />
  );
}

function StrandRowItem({ strand, row }: { strand: PublicStrand; row?: IntelRow }) {
  const live = strand.status === "live";
  const value = live && row && !row.is_null ? row.value : null;

  return (
    <li
      data-strand-status={strand.status}
      className={[
        "flex gap-3 rounded-2xl p-3 sm:p-4",
        live
          ? "border border-border/70 bg-card/70"
          : "border border-dashed border-border/60 bg-transparent",
      ].join(" ")}
    >
      <StatusDot status={strand.status} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <p
            className={[
              "font-display text-sm font-semibold sm:text-base",
              live ? "text-foreground" : "text-muted-foreground",
            ].join(" ")}
          >
            {strand.label}
          </p>
          <span className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {STRAND_STATUS_LABEL[strand.status]}
          </span>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{strand.meaning}</p>

        {live ? (
          <div className="mt-3 flex items-center gap-3">
            <div
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted/50"
              role="img"
              aria-label={`${strand.label}: ${pct(value)} of 100`}
            >
              <div
                className="h-full rounded-full bg-primary/80 motion-safe:transition-[width] motion-safe:duration-700"
                style={{ width: `${bar(value)}%` }}
              />
            </div>
            <span className="shrink-0 font-display text-sm tabular-nums text-foreground/80">
              {pct(value)}
            </span>
          </div>
        ) : (
          <p className="mt-2 text-xs leading-relaxed text-foreground/70">{strand.statusSentence}</p>
        )}
      </div>
    </li>
  );
}

export default function DnaFramework({ rows = [] }: { rows?: IntelRow[] }) {
  const byCode = new Map(rows.map((r) => [r.intelligence_code, r]));

  return (
    <section aria-labelledby="dna-framework-heading" className="calm-card p-5 sm:p-6">
      <h3
        id="dna-framework-heading"
        className="font-display text-lg font-semibold sm:text-xl"
      >
        Career DNA™ — the framework
      </h3>
      <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
        {CAREER_DNA_FRAMEWORK_INTRO}
      </p>

      <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-primary" /> live
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full border border-dashed border-muted-foreground/70"
          />{" "}
          reserved
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full border border-dashed border-muted-foreground/70"
          />{" "}
          in build
        </span>
      </p>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {CAREER_DNA_STRANDS.map((strand) => (
          <StrandRowItem
            key={strand.label}
            strand={strand}
            row={strand.intelligenceCode ? byCode.get(strand.intelligenceCode) : undefined}
          />
        ))}
      </ul>
    </section>
  );
}
